
import { useCallback, useEffect, useMemo, useState } from 'react';
import { djangoClient, setApiKey, getApiKey, clearApiKey, setUserEmail } from '@/src/helpers/apis';
import { SignInParams, SignUpParams } from './auth.types';
import { AuthContext, AuthContextApi, AuthUser, AuthSession } from './auth-context';

/**
 * AuthProvider - Manages authentication state and provides auth methods to the app
 *
 * This provider wraps the entire app and handles:
 * - User session management
 * - Authentication state (loading, user, session)
 * - Sign in/up/out operations
 * - API key validation on mount
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  // State management for authentication
  const [session, setSession] = useState<AuthSession | null>(null); // Current user session
  const [user, setUser] = useState<AuthUser | null>(null); // Current user data
  const [loading, setLoading] = useState(true); // Loading state while checking auth

  /**
   * Effect hook to initialize authentication state
   * Checks for existing API key in sessionStorage and validates it
   */
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after unmount

    // Async function to validate existing session
    (async () => {
      try {
        const apiKey = getApiKey();

        // If no API key found, user is not authenticated
        if (!apiKey) {
          if (isMounted) {
            setSession(null);
            setUser(null);
            setLoading(false);
          }
          return;
        }

        // Validate API key by calling /api/auth/me/
        const { data } = await djangoClient.get<AuthUser>('/api/auth/me/');

        // Only update state if component is still mounted
        if (!isMounted) return;

        // Set session and user state
        setSession({ apiKey });
        setUser(data);
        setLoading(false);
      } catch (error) {
        console.error('Error validating session:', error);
        if (isMounted) {
          // Clear invalid session
          clearApiKey();
          setSession(null);
          setUser(null);
          setLoading(false);
        }
      }
    })();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Sign in with email and password
   * @param params - Object containing email and password
   * @returns Promise with error message if sign-in fails
   */
  const signInWithPassword = useCallback(
    async ({ email, password }: SignInParams) => {
      try {
        setLoading(true);
        const { data } = await djangoClient.post<{
          email: string;
          username: string;
          api_key: string;
          vault_id?: string | null;
          xrp_address?: string | null;
        }>('/api/auth/signin/', {
          email,
          password,
        });

        // Store API key and email in sessionStorage
        setApiKey(data.api_key);
        setUserEmail(data.email);

        // Update state with user data and session
        setUser({
          email: data.email,
          username: data.username,
          vault_id: data.vault_id || null,
          xrp_address: data.xrp_address || null,
        });
        setSession({ apiKey: data.api_key });

        return { error: null };
      } catch (error: any) {
        console.error('Sign in error:', error);
        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          'An unexpected error occurred';
        return { error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Sign up with email, password, and invitation code
   * @param params - Object containing email, password, and invitation_id
   * @returns Promise with error message if sign-up fails
   */
  const signUpWithPassword = useCallback(
    async ({ email, password, invitation_id }: SignUpParams) => {
      try {
        setLoading(true);
        const { data } = await djangoClient.post<{
          email: string;
          username: string;
          api_key: string;
          vault_id?: string | null;
          xrp_address?: string | null;
        }>('/api/auth/signup/', {
          email,
          password,
          invitation_id,
        });

        // Store API key and email in sessionStorage
        setApiKey(data.api_key);
        setUserEmail(data.email);

        // Update state with user data and session
        setUser({
          email: data.email,
          username: data.username,
          vault_id: data.vault_id || null,
          xrp_address: data.xrp_address || null,
        });
        setSession({ apiKey: data.api_key });

        return { error: null };
      } catch (error: any) {
        console.error('Sign up error:', error);
        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          'An unexpected error occurred';
        return { error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Sign out the current user
   * Clears API key from sessionStorage and resets state
   */
  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      // Clear API key from sessionStorage
      clearApiKey();
      // Reset state
      setSession(null);
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Memoized context value to prevent unnecessary re-renders
   * Only updates when auth state or methods change
   */
  const value = useMemo<AuthContextApi>(
    () => ({
      user, // Current user data
      session, // Current session data
      loading, // Loading state
      signInWithPassword, // Sign in method
      signUpWithPassword, // Sign up method
      signOut, // Sign out method
    }),
    [user, session, loading, signInWithPassword, signUpWithPassword, signOut]
  );

  // Provide auth context to all child components
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
